import { useCallback, useEffect, useState } from 'react';
import type { DailyReportDTO, AggregatedReportDTO, ReportPeriod, ReportTopItem, PaymentMethod } from '@scan2eat/shared';
import { formatMoney, PAYMENT_METHODS } from '@scan2eat/shared';
import { apiFetch } from '../api/client.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { HourlyChart } from '../components/HourlyChart.js';
import { Icon } from '../components/Icon.js';
import { StatTile } from '../components/StatTile.js';
import { labels } from '../labels.js';
import styles from './DailyReportPage.module.css';

type Tab = 'daily' | ReportPeriod;

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'daily', label: labels.report.tabDaily },
  { key: 'weekly', label: labels.report.tabWeekly },
  { key: 'monthly', label: labels.report.tabMonthly },
  { key: 'yearly', label: labels.report.tabYearly },
];

const PERIOD_DAYS: Record<ReportPeriod, number> = {
  weekly: 7,
  monthly: 30,
  yearly: 365,
};

/** Density rule of the design system: no table beyond five rows without "see all". */
const TABLE_PREVIEW_ROWS = 5;

export function DailyReportPage() {
  const [tab, setTab] = useState<Tab>('daily');
  const [date, setDate] = useState(() => localDate(new Date()));

  return (
    <div className="page">
      {tab === 'daily' ? (
        <DailyView date={date} setDate={setDate} tabs={<TabBar active={tab} onChange={setTab} />} />
      ) : (
        <AggregatedView period={tab} date={date} setDate={setDate} tabs={<TabBar active={tab} onChange={setTab} />} />
      )}
    </div>
  );
}

/* -- Tabs and date navigation -- */

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="segmented" role="tablist" aria-label={labels.report.periodLabel}>
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          className="segmented__item"
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Stepper({ label, onPrevious, onNext, previousLabel, nextLabel }: {
  label: string;
  onPrevious: () => void;
  onNext: () => void;
  previousLabel: string;
  nextLabel: string;
}) {
  return (
    <div className={styles.stepper}>
      <button type="button" className="btn-icon btn-icon--quiet btn-icon--sm" onClick={onPrevious} aria-label={previousLabel}>
        <Icon name="chevron-left" size={18} />
      </button>
      <span className={styles.stepperLabel}>{label}</span>
      <button type="button" className="btn-icon btn-icon--quiet btn-icon--sm" onClick={onNext} aria-label={nextLabel}>
        <Icon name="chevron-right" size={18} />
      </button>
    </div>
  );
}

function ReportHeader({ title, tabs, children }: { title: string; tabs: React.ReactNode; children: React.ReactNode }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerTop}>
        <h1 className="page-title">{title}</h1>
        {tabs}
      </div>
      <div className={styles.headerTools}>{children}</div>
    </header>
  );
}

function LoadingGrid() {
  return (
    <>
      <div className="kpi-grid">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '6rem', borderRadius: 'var(--radius-lg)' }} />)}
      </div>
      <div className={styles.split}>
        <div className="skeleton" style={{ height: '18rem', borderRadius: 'var(--radius-lg)' }} />
        <div className="skeleton" style={{ height: '18rem', borderRadius: 'var(--radius-lg)' }} />
      </div>
    </>
  );
}

/* -- Daily report -- */

function DailyView({ date, setDate, tabs }: { date: string; setDate: (d: string) => void; tabs: React.ReactNode }) {
  const [report, setReport] = useState<DailyReportDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return apiFetch<DailyReportDTO>(`/admin/reports/daily?date=${date}`)
      .then((r) => { setReport(r); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const shiftDay = (delta: number) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(localDate(d));
    setShowAllOrders(false);
  };

  const exportPdf = async () => {
    setPdfError(false);
    try {
      await apiBlobDownload(`/admin/reports/daily.pdf?date=${date}`, `bilan-${date}.pdf`);
    } catch {
      setPdfError(true);
    }
  };

  const dayLabel = capitalize(new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
  const orders = report?.completedOrders ?? [];
  const visibleOrders = showAllOrders ? orders : orders.slice(0, TABLE_PREVIEW_ROWS);
  const prep = report?.timing.averagePreparationSeconds;

  return (
    <>
      <ReportHeader title={labels.report.title} tabs={tabs}>
        <Stepper
          label={dayLabel}
          onPrevious={() => shiftDay(-1)}
          onNext={() => shiftDay(1)}
          previousLabel={labels.report.previousDay}
          nextLabel={labels.report.nextDay}
        />
        <button onClick={() => void exportPdf()} className="btn-ghost">
          <Icon name="download" size={18} />
          {labels.report.exportPdf}
        </button>
      </ReportHeader>

      {pdfError && (
        <p role="alert" className="notice notice--warning">
          <Icon name="alert" size={18} />
          {labels.tables.pdfUnavailable}
        </p>
      )}

      {loading ? (
        <LoadingGrid />
      ) : loadError ? (
        <ErrorState onRetry={load} />
      ) : !report || report.orders.completed === 0 ? (
        <div className="card"><EmptyState icon="chart" text={labels.report.empty} /></div>
      ) : (
        <>
          <div className="kpi-grid">
            <StatTile icon="cash" label={labels.report.revenue} value={formatMoney(report.revenue.totalMinor, report.currency)} />
            <StatTile
              icon="check-circle"
              label={labels.report.completed}
              value={report.orders.completed}
              meta={prep != null ? labels.report.averagePreparation(Math.round(prep / 60)) : undefined}
            />
            <StatTile icon="close" label={labels.report.cancelled} value={report.orders.cancelled} tone={report.orders.cancelled > 0 ? 'alert' : 'default'} />
            <StatTile icon="bag" label={labels.report.averageBasket} value={formatMoney(report.orders.averageBasketMinor, report.currency)} />
          </div>

          <div className={styles.split}>
            <section className="card">
              <header className="section-header">
                <h2 className="section-title">{labels.report.hourly}</h2>
              </header>
              <HourlyChart hourly={report.hourly} currency={report.currency} />
            </section>
            <PaymentsCard byMethod={report.revenue.byPaymentMethod} total={report.revenue.totalMinor} currency={report.currency} />
          </div>

          <div className={styles.split}>
            {orders.length > 0 && (
              <section className="card card--flush">
                <header className="card-header">
                  <h2 className="card-title">{labels.report.completedOrders}</h2>
                  <span className="count">{orders.length}</span>
                </header>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{labels.report.orderNumber}</th>
                        <th>{labels.report.orderTime}</th>
                        <th className="num">{labels.report.orderTotal}</th>
                        <th>{labels.report.orderPayment}</th>
                        <th><span className="visually-hidden">{labels.report.orderReceipt}</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOrders.map((order) => (
                        <tr key={order.orderId}>
                          <td>
                            <strong>#{order.number}</strong>
                            <span className="text-xs text-muted" style={{ display: 'block' }}>{labels.app.tableLabel(order.tableLabel)}</span>
                          </td>
                          <td className="tabular text-muted">
                            {new Date(order.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="num" style={{ fontWeight: 'var(--weight-semibold)' }}>{formatMoney(order.totalMinor, report.currency)}</td>
                          <td className="text-muted">
                            {order.paymentMethod ? labels.report.paymentMethod[order.paymentMethod] : labels.report.noPaymentMethod}
                          </td>
                          <td className="num">
                            {order.hasInvoice && (
                              <button
                                type="button"
                                className="btn-icon btn-icon--quiet btn-icon--sm"
                                aria-label={labels.report.downloadReceiptFor(order.number)}
                                title={labels.report.downloadReceipt}
                                onClick={() => void apiBlobDownload(
                                  `/admin/orders/${order.orderId}/invoice.pdf`,
                                  `${order.invoiceNumber ?? `commande-${order.number}`}.pdf`,
                                )}
                              >
                                <Icon name="receipt" size={18} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {orders.length > TABLE_PREVIEW_ROWS && (
                  <footer className="card-footer" style={{ justifyContent: 'center' }}>
                    <button type="button" className="btn-link" onClick={() => setShowAllOrders((v) => !v)} aria-expanded={showAllOrders}>
                      {showAllOrders ? labels.common.seeLess : labels.report.seeAllOrders(orders.length)}
                      <Icon name={showAllOrders ? 'chevron-left' : 'chevron-down'} size={16} />
                    </button>
                  </footer>
                )}
              </section>
            )}

            <TopItemsCard items={report.topItems} currency={report.currency} />
          </div>
        </>
      )}
    </>
  );
}

/* -- Aggregated report -- */

function AggregatedView({ period, date, setDate, tabs }: { period: ReportPeriod; date: string; setDate: (d: string) => void; tabs: React.ReactNode }) {
  const [report, setReport] = useState<AggregatedReportDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const days = PERIOD_DAYS[period];

  const computeRange = useCallback((anchor: string) => {
    const to = new Date(`${anchor}T12:00:00`);
    const from = new Date(to);
    from.setDate(from.getDate() - (days - 1));
    return { from: localDate(from), to: localDate(to) };
  }, [days]);

  const load = useCallback(() => {
    setLoading(true);
    const { from, to } = computeRange(date);
    return apiFetch<AggregatedReportDTO>(`/admin/reports/aggregate?period=${period}&from=${from}&to=${to}`)
      .then((r) => { setReport(r); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [date, period, computeRange]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta: number) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta * days);
    setDate(localDate(d));
  };

  const { from, to } = computeRange(date);
  const short = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  const rangeLabel = `${short(from)} - ${short(to)}`;
  const maxRevenue = Math.max(...(report?.periods.map((p) => p.revenueMinor) ?? [0]), 1);

  return (
    <>
      <ReportHeader title={labels.report.aggregatedTitle[period]} tabs={tabs}>
        <Stepper
          label={rangeLabel}
          onPrevious={() => shift(-1)}
          onNext={() => shift(1)}
          previousLabel={labels.report.previousPeriod}
          nextLabel={labels.report.nextPeriod}
        />
      </ReportHeader>

      {loading ? (
        <LoadingGrid />
      ) : loadError ? (
        <ErrorState onRetry={load} />
      ) : !report || report.totals.completedOrders === 0 ? (
        <div className="card"><EmptyState icon="chart" text={labels.report.emptyAggregated} /></div>
      ) : (
        <>
          <div className="kpi-grid">
            <StatTile icon="cash" label={labels.report.revenue} value={formatMoney(report.totals.revenueMinor, report.currency)} />
            <StatTile icon="check-circle" label={labels.report.completed} value={report.totals.completedOrders} />
            <StatTile icon="close" label={labels.report.cancelled} value={report.totals.cancelledOrders} tone={report.totals.cancelledOrders > 0 ? 'alert' : 'default'} />
            <StatTile icon="bag" label={labels.report.averageBasket} value={formatMoney(report.totals.averageBasketMinor, report.currency)} />
          </div>

          <section className={`card card--flush ${styles.block}`}>
            <header className="card-header">
              <h2 className="card-title">{labels.report.breakdown[period]}</h2>
            </header>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{labels.report.periodLabel}</th>
                    <th className="num">{labels.report.revenue}</th>
                    <th className="num">{labels.report.completed}</th>
                    <th className="num">{labels.report.cancelled}</th>
                    <th className="num">{labels.report.averageBasket}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.periods.map((p) => (
                    <tr key={p.periodLabel}>
                      <td>
                        <span style={{ fontWeight: 'var(--weight-semibold)' }}>{p.periodLabel}</span>
                        <span className={styles.inlineBar} style={{ width: `${(p.revenueMinor / maxRevenue) * 100}%` }} aria-hidden="true" />
                      </td>
                      <td className="num" style={{ fontWeight: 'var(--weight-semibold)' }}>{formatMoney(p.revenueMinor, report.currency)}</td>
                      <td className="num">{p.completedOrders}</td>
                      <td className="num">{p.cancelledOrders}</td>
                      <td className="num">{formatMoney(p.averageBasketMinor, report.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className={styles.split}>
            <TopItemsCard items={report.topItems} currency={report.currency} />
            <PaymentsCard byMethod={report.byPaymentMethod} total={report.totals.revenueMinor} currency={report.currency} />
          </div>
        </>
      )}
    </>
  );
}

/* -- Shared cards -- */

function TopItemsCard({ items, currency }: { items: ReportTopItem[]; currency: string }) {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;
  const max = Math.max(...items.map((i) => i.quantity), 1);
  const visible = showAll ? items : items.slice(0, TABLE_PREVIEW_ROWS);

  return (
    <section className="card card--flush">
      <header className="card-header">
        <h2 className="card-title">{labels.report.topItems}</h2>
      </header>
      <ol className={styles.ranking}>
        {visible.map((item, i) => (
          <li key={item.menuItemId}>
            <span className={styles.rank}>{i + 1}</span>
            <div className="grow">
              <div className="row row--between">
                <span className="truncate" style={{ fontWeight: 'var(--weight-semibold)' }}>{item.name}</span>
                <span className="tabular text-sm" style={{ fontWeight: 'var(--weight-semibold)' }}>{formatMoney(item.revenueMinor, currency)}</span>
              </div>
              <div className={styles.meter} aria-hidden="true">
                <span style={{ width: `${(item.quantity / max) * 100}%` }} />
              </div>
              <span className="text-xs text-muted">{labels.report.soldCount(item.quantity)}</span>
            </div>
          </li>
        ))}
      </ol>
      {items.length > TABLE_PREVIEW_ROWS && (
        <footer className="card-footer" style={{ justifyContent: 'center' }}>
          <button type="button" className="btn-link" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
            {showAll ? labels.common.seeLess : labels.common.seeAll}
          </button>
        </footer>
      )}
    </section>
  );
}

function PaymentsCard({ byMethod, total, currency }: { byMethod: Record<PaymentMethod, number>; total: number; currency: string }) {
  const rows = PAYMENT_METHODS
    .map((method) => ({ method, amount: byMethod[method] ?? 0 }))
    .sort((a, b) => b.amount - a.amount);

  return (
    <section className="card">
      <header className="section-header">
        <h2 className="section-title">{labels.report.payments}</h2>
      </header>
      <ul className={styles.payments}>
        {rows.map(({ method, amount }) => {
          const share = total > 0 ? Math.round((amount / total) * 100) : 0;
          return (
            <li key={method} className={amount === 0 ? styles.paymentZero : undefined}>
              <div className="row row--between">
                <span className="row" style={{ gap: 'var(--space-2)' }}>
                  <Icon name={method === 'MOBILE_MONEY' ? 'phone' : method === 'CARD' ? 'receipt' : 'cash'} size={16} className="text-muted" />
                  {labels.report.paymentMethod[method]}
                </span>
                <span className="tabular" style={{ fontWeight: 'var(--weight-semibold)' }}>{formatMoney(amount, currency)}</span>
              </div>
              <div className={styles.meter} aria-hidden="true">
                <span style={{ width: `${share}%` }} />
              </div>
              <span className="text-xs text-muted tabular">{share} %</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

async function apiBlobDownload(path: string, filename: string): Promise<void> {
  const { apiFetchBlob } = await import('../api/client.js');
  const blob = await apiFetchBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function localDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
